import Vue from 'vue';
import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';

export const TryVueSet = (obj, key, value) => {
  try {
    Vue.set(obj, key, value);
  } catch (error) {
    // ignore because conversation its not anymore in the list
  }
};

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

// Freeze a conversation row before it enters allConversations. Vue 2's observe()
// skips non-extensible objects, so a frozen row never gets reactive getters or
// dependency tracking installed. This removes the per-row addDep/reactiveGetter
// cost that dominates frame time when the list getters iterate thousands of rows
// under WebSocket bursts. The freeze is shallow (top-level only) — nested objects
// are never observed either, because the row itself is never observed. Consequence:
// every row change must go through replaceConversationInList, since a frozen row
// silently drops direct writes.
export const freezeRow = conversation => Object.freeze(conversation);

// The open conversation kept in currentConversation must stay reactive (its
// message thread grows and needs deep reactivity) and must never be a frozen list
// row. Clone the nested objects that get mutated in place (meta via TryVueSet,
// additional_attributes) plus messages, so writes to currentConversation never
// reach into the frozen row's shared references.
export const cloneConversationForCurrent = conversation => ({
  ...conversation,
  meta: { ...(conversation.meta || {}) },
  additional_attributes: { ...(conversation.additional_attributes || {}) },
  messages: [...(conversation.messages || [])],
});

// Rebuild the id -> index lookup from scratch. Called by mutations that replace,
// clear, filter or reorder the whole list. Plain Map: Vue 2 does not make Maps
// reactive, which is fine — it is an internal lookup, never rendered.
export const rebuildConversationIndex = _state => {
  const index = new Map();
  _state.allConversations.forEach((conversation, position) => {
    index.set(conversation.id, position);
  });
  _state.conversationIndexById = index;
};

// Resolve a conversation's position in allConversations. O(1) via the id -> index
// map when present; falls back to a scan for hand-built test states that omit the
// map. Row-content changes keep the same slot, so the map stays valid across
// replaceConversationInList without a rebuild.
export const getRowIndex = (_state, conversationId) => {
  const map = _state.conversationIndexById;
  if (map instanceof Map) {
    const index = map.get(conversationId);
    return index === undefined ? -1 : index;
  }
  return _state.allConversations.findIndex(c => c.id === conversationId);
};

// Replace a conversation row with a frozen shallow clone that `applyPatch` has
// mutated. This is the only correct way to change a row: rows are frozen, so
// direct writes are silently dropped; replacing the array slot fires the array's
// Vue dependency and re-runs the list getters with the new values. The row keeps
// its index, so the id -> index map stays valid. No-op (returns undefined) when
// the row is absent, preserving the previous silent behaviour.
export const replaceConversationInList = (
  _state,
  conversationId,
  applyPatch
) => {
  const index = getRowIndex(_state, conversationId);
  if (index === -1) return undefined;
  const draft = { ..._state.allConversations[index] };
  applyPatch(draft);
  const next = freezeRow(draft);
  Vue.set(_state.allConversations, index, next);
  return next;
};

// Marks merged-away (loser) conversations instead of dropping them from the list:
// stamps `merged_into_id` (the survivor's display_id) + flips status to resolved,
// so they keep showing with the "Merged" badge under "All" and fall out of active
// filters (open/mine) exactly as a refetch would render them — rather than
// vanishing from every view until a manual reload.
export const stampMergedConversationsInList = (
  _state,
  mergedConversationIds,
  survivorDisplayId
) => {
  if (!mergedConversationIds?.length) return;

  mergedConversationIds.forEach(id => {
    replaceConversationInList(_state, id, draft => {
      draft.merged_into_id = survivorDisplayId;
      draft.status = 'resolved';
    });
  });
};

import wootConstants from 'dashboard/constants/globals';

export const filterByStatus = (chatStatus, filterStatus) => {
  if (filterStatus === wootConstants.STATUS_TYPE.ALL) return true;
  if (filterStatus === wootConstants.STATUS_TYPE.WAITING_FOR_AGENT_REPLY)
    return chatStatus === wootConstants.STATUS_TYPE.OPEN;
  return chatStatus === filterStatus;
};

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const { inboxId, status, labels = [], teamId, conversationType } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );

  return shouldFilter;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    if (!a.waiting_since || !b.waiting_since) {
      if (!a.waiting_since && !b.waiting_since) {
        return sortFunc(a.created_at, b.created_at);
      }
      return sortFunc(a.waiting_since ? 0 : 1, b.waiting_since ? 0 : 1);
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};

// Chronological comparator for conversation messages. `created_at` is epoch
// SECONDS (a number), so compare the numbers directly: `new Date(seconds)` would
// parse them as milliseconds — the ordering happened to survive (the transform
// is monotonic), but the explicit numeric compare is correct and avoids the
// latent footgun if a created_at ever arrives as a non-numeric value.
export const compareMessagesByCreatedAt = (a, b) => a.created_at - b.created_at;

export const MAX_SUBSCRIBED_CONVERSATIONS = 5;
export const SUBSCRIPTION_CONFIRM_TIMEOUT_MS = 5000;
export const MAX_SUBSCRIPTION_RETRIES = 2;

// Merge incoming conversation metadata while preserving the local message
// state (messages, dataFetched, allMessagesLoaded). Used when the API or
// WebSocket delivers updated conversation attributes that should NOT
// overwrite messages already in the store.
export const mergeConversationPreservingMessages = (existing, incoming) => {
  const attrs = { ...incoming };
  delete attrs.messages;
  delete attrs.dataFetched;
  delete attrs.allMessagesLoaded;
  // Broadcast payloads carry a truncated last_non_activity_message stand-in
  // (flagged broadcast_preview): never let it replace a same-or-newer full
  // copy from REST — and on a created_at tie, a full copy replaces a snippet.
  const existingPreview = existing.last_non_activity_message;
  const incomingPreview = attrs.last_non_activity_message;
  if (existingPreview) {
    let keepExisting;
    if (!incomingPreview) keepExisting = true;
    else if (incomingPreview.created_at < existingPreview.created_at)
      keepExisting = true;
    else if (incomingPreview.created_at > existingPreview.created_at)
      keepExisting = false;
    else
      keepExisting =
        !!incomingPreview.broadcast_preview ||
        !existingPreview.broadcast_preview;
    if (keepExisting) delete attrs.last_non_activity_message;
  }
  return {
    ...existing,
    ...attrs,
  };
};
